import SEO from "../../components/SEO";
export const PrivacyPage = () => {
	return (
		<div className="w-full min-h-screen bg-gradient-to-br from-cyan-100 to-fuchsia-200 py-12 px-5"><SEO title="Privacy Policy" description="Parcelin privacy policy. How we collect, use and protect your personal information under POPIA." path="/privacy" />
			<div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 md:p-12">
				<h1 className="text-4xl font-bold text-purple-800 mb-6">Privacy Policy</h1>
				<p className="text-gray-600 mb-8">Last Updated: November 2024</p>

				<div className="space-y-8 text-gray-700">
					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">1. Introduction</h2>
						<p className="mb-4">
							Parcelin ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
							use, disclose, and safeguard your information when you use our parcel delivery platform and services.
						</p>
						<p className="mb-4">
							We comply with the Protection of Personal Information Act (POPIA) and other applicable South African data protection laws.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">2. Information We Collect</h2>
						
						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">2.1 Personal Information</h3>
						<p className="mb-4">We collect personal information that you provide to us, including:</p>
						<ul className="list-disc pl-6 space-y-2 mb-4">
							<li>Name and contact information (email, phone number)</li>
							<li>Physical addresses (pickup and delivery locations)</li>
							<li>Payment information</li>
							<li>Account credentials</li>
							<li>Profile information and preferences</li>
						</ul>

						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">2.2 Parcel Information</h3>
						<ul className="list-disc pl-6 space-y-2 mb-4">
							<li>Parcel size and type</li>
							<li>Pickup and delivery details</li>
							<li>Tracking information</li>
							<li>Delivery instructions</li>
						</ul>

						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">2.3 Automatically Collected Information</h3>
						<ul className="list-disc pl-6 space-y-2 mb-4">
							<li>Device information (IP address, browser type, operating system)</li>
							<li>Usage data (pages visited, features used, time spent)</li>
							<li>Location data (with your permission)</li>
							<li>Cookies and similar tracking technologies</li>
						</ul>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">3. How We Use Your Information</h2>
						<p className="mb-4">We use the collected information for the following purposes:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>To provide and maintain our delivery services</li>
							<li>To process bookings and payments</li>
							<li>To match senders with appropriate drivers</li>
							<li>To provide real-time tracking and notifications</li>
							<li>To communicate with you about your deliveries</li>
							<li>To improve our services and user experience</li>
							<li>To detect and prevent fraud or abuse</li>
							<li>To comply with legal obligations</li>
							<li>To send marketing communications (with your consent)</li>
						</ul>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">4. Information Sharing and Disclosure</h2>
						<p className="mb-4">We may share your information with:</p>
						
						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">4.1 Drivers</h3>
						<p className="mb-4">
							We share necessary information with drivers to facilitate pickup and delivery, including your name, phone number, 
							and pickup/delivery addresses.
						</p>

						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">4.2 Service Providers</h3>
						<p className="mb-4">
							We work with third-party service providers for payment processing, SMS notifications, email services, and analytics. 
							These providers have access only to information necessary to perform their functions.
						</p>

						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">4.3 Legal Requirements</h3>
						<p className="mb-4">
							We may disclose your information if required by law, court order, or government request, or to protect our rights, 
							property, or safety.
						</p>

						<h3 className="text-xl font-semibold text-purple-700 mb-3 mt-4">4.4 Business Transfers</h3>
						<p className="mb-4">
							In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">5. Data Security</h2>
						<p className="mb-4">
							We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, 
							alteration, disclosure, or destruction. These measures include:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Encryption of data in transit and at rest</li>
							<li>Secure payment processing</li>
							<li>Regular security assessments</li>
							<li>Access controls and authentication</li>
							<li>Employee training on data protection</li>
						</ul>
						<p className="mt-4">
							However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">6. Your Rights Under POPIA</h2>
						<p className="mb-4">Under South African law, you have the right to:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Access your personal information</li>
							<li>Correct inaccurate or incomplete information</li>
							<li>Request deletion of your information (subject to legal requirements)</li>
							<li>Object to processing of your information</li>
							<li>Withdraw consent for marketing communications</li>
							<li>Lodge a complaint with the Information Regulator</li>
						</ul>
						<p className="mt-4">
							To exercise these rights, please contact us at privacy@parcelin.co.za
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">7. Data Retention</h2>
						<p className="mb-4">
							We retain your personal information for as long as necessary to provide our services and comply with legal obligations. 
							Delivery records are typically retained for 7 years for accounting and legal purposes. You may request deletion of your 
							account and associated data at any time, subject to legal retention requirements.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">8. Cookies and Tracking Technologies</h2>
						<p className="mb-4">
							We use cookies and similar technologies to enhance your experience, analyze usage patterns, and deliver personalized content. 
							You can control cookie preferences through your browser settings, though some features may not function properly if cookies are disabled.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">9. Third-Party Links</h2>
						<p className="mb-4">
							Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these 
							third parties. We encourage you to review their privacy policies.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">10. Children's Privacy</h2>
						<p className="mb-4">
							Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. 
							If you believe we have collected information from a child, please contact us immediately.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">11. Changes to This Privacy Policy</h2>
						<p className="mb-4">
							We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through our platform. 
							The "Last Updated" date at the top indicates when the policy was last revised.
						</p>
					</section>

					<section>
						<h2 className="text-2xl font-bold text-purple-800 mb-4">12. Contact Us</h2>
						<p className="mb-4">
							If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
						</p>
						<p className="mb-2">Email: privacy@parcelin.co.za</p>
						<p className="mb-2">Phone: 069 904 2846</p>
						<p className="mb-2">Address: [Your Business Address]</p>
						<p className="mt-4">
							Information Regulator (South Africa): <a href="https://inforegulator.org.za" className="text-purple-600 hover:text-purple-700" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a>
						</p>
					</section>
				</div>

				<div className="mt-12 pt-8 border-t border-gray-200 text-center">
					<p className="text-gray-600">
						By using Parcelin, you acknowledge that you have read and understood this Privacy Policy.
					</p>
				</div>
			</div>
		</div>
	);
};
